/**
 * O documento da saída em blocos, para a mesa única (E202.F1, GUS-145, ADR-0145 §1). Espelha
 * `apps/api/types/documento_da_saida.py`.
 *
 * `GET /casos/:id/correcao/documento?saida=exame|riscos` devolve o que `…/trechos` devolve (o vigente
 * de cada trecho, as desatualizadas e as não aplicadas) e o papel em seções e blocos, lido do DOCX
 * composto: o que a mesa mostra é o que o PDF imprime. A tela não compõe nada.
 */
import axios, { type AxiosInstance } from 'axios';

import {
  CorrecaoDoRascunhoError,
  motivoDoErro,
  type SaidaDaEdicao,
  type TrechoNaTelaData,
  type TrechosDoLaudoData,
} from './correcaoDoRascunho';

export type TipoDoBloco =
  | 'abertura'
  | 'secao'
  | 'titulo'
  | 'subtitulo'
  | 'paragrafo'
  | 'nota'
  | 'itens'
  | 'caixa'
  | 'tabela'
  | 'quadro'
  | 'grade'
  | 'quebra';

export type Alinhamento = 'esquerda' | 'centro' | 'direita' | 'justificado';
/** A área da mesa que registra o conteúdo da seção com fundamento (ADR-0145 §8). */
export type AtalhoDaMesa = 'conclusao' | 'fotografias';

/** De onde vem o que o bloco imprime (E203.F2, ADR-0146 §2). */
export type TipoDaOrigem =
  | 'declarado'
  | 'lido'
  | 'credito'
  | 'situacao'
  | 'conclusao'
  | 'facial'
  | 'portal'
  | 'risco'
  | 'prova'
  | 'catalogo';

/** A revisão do perito com fundamento (ADR-0136), espelho de `TipoDaRevisao` do servidor. */
export type RevisaoDaOrigem =
  | 'face'
  | 'contraprova'
  | 'suprimento'
  | 'diligencias'
  | 'autorizacao'
  | 'credito'
  | 'integridade';

/**
 * O dado que compõe a tabela, a linha ou o texto protegido: o clique abre o painel do dado por ela.
 * `chave` é o dado dentro do tipo (o campo da ficha, `cadastro`, `protocolo`, o nº do risco…);
 * `linha`, a linha de conferência; `revisao`, a revisão com fundamento que já cobre o resultado.
 */
export interface OrigemDoBlocoData {
  tipo: TipoDaOrigem;
  chave: string;
  linha: string | null;
  revisao: RevisaoDaOrigem | null;
  /**
   * E203.F4.C (F-E203.F4-05): o que o documento imprime ali, para o painel abrir pelo vigente. Na
   * linha do mapa de risco, o grau (`alto|medio|baixo`) e o fato; na linha de identidade, o resultado
   * (`consistente|inconclusivo|divergente`). `null` (ou ausente) quando não deu para ler.
   */
  valor?: string | null;
  texto?: string | null;
  /**
   * E205.F3.C01 (F-E205.F3-01): no dado lido aberto por uma pendência ou por "Rever leitura", o
   * referente do campo (a CCB): com duas CCBs o mesmo campo existe uma vez por CCB, e o painel abre
   * exatamente o par (campo, referente). Ausente (o documento impresso), o painel abre pela chave.
   */
  referente?: string | null;
}

export interface TrechoDoBlocoData {
  texto: string;
  negrito: boolean;
  italico: boolean;
  mono: boolean;
  sobrescrito: boolean;
  cor: string | null;
  fundo: string | null;
  tamanho: number | null;
}

export interface ImagemDoBlocoData {
  src: string;
  largura: number;
  altura: number;
  largura_no_papel: number | null;
}

export interface ParagrafoDoBlocoData {
  trechos: TrechoDoBlocoData[];
  imagens: ImagemDoBlocoData[];
  item: boolean;
  alinhamento: Alinhamento | null;
}

export interface CelulaDoBlocoData {
  paragrafos: ParagrafoDoBlocoData[];
  grades: GradeDoBlocoData[];
  fundo: string | null;
  colunas: number;
}

export interface LinhaDoBlocoData {
  celulas: CelulaDoBlocoData[];
  cabecalho: boolean;
  /** E203.F2: a linha com dado próprio (o campo do quadro, a linha da conferência, o risco). */
  origem?: OrigemDoBlocoData | null;
}

export interface GradeDoBlocoData {
  linhas: LinhaDoBlocoData[];
  larguras: number[];
}

export interface ParteDoBlocoData {
  /** O trecho de redação do inventário; sem parágrafos, a edição o tirou do documento. */
  trecho_id: string | null;
  paragrafos: ParagrafoDoBlocoData[];
}

export interface BlocoDoDocumentoData {
  tipo: TipoDoBloco;
  titulo: string | null;
  numero: string | null;
  caixa_alta: boolean;
  partes: ParteDoBlocoData[];
  grade: GradeDoBlocoData | null;
  /** E203.F2: de onde vem o que o bloco imprime, quando não é só texto de redação. */
  origem?: OrigemDoBlocoData | null;
}

export interface SecaoDoDocumentoData {
  ancora: string;
  chave: string | null;
  numero: string | null;
  titulo: string | null;
  atalho: AtalhoDaMesa | null;
  blocos: BlocoDoDocumentoData[];
}

export interface DocumentoDaSaidaData extends TrechosDoLaudoData {
  secoes: SecaoDoDocumentoData[];
}

/** A âncora de uma seção do documento começa assim: o índice marca "Exame" com ela. */
export const PREFIXO_DA_ANCORA = 'documento-secao-';

/**
 * E206.F3 (GUS-296 item 4): o servidor monta o documento na hora (compõe o DOCX e o converte em
 * blocos), e na primeira abertura disputa com a preparação do pacote: no QA de 01/11 levou ~16 s, e o
 * prazo de 15 s do cliente virava "Não foi possível montar o documento". O documento tem prazo próprio.
 */
export const DOCUMENTO_TIMEOUT_MS = 60_000;

export async function lerDocumentoDaSaida(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDaEdicao,
): Promise<DocumentoDaSaidaData> {
  try {
    const resposta = await client.get<DocumentoDaSaidaData>(`/casos/${caseId}/correcao/documento`, {
      params: { saida },
      timeout: DOCUMENTO_TIMEOUT_MS,
    });
    return resposta.data;
  } catch (err) {
    // O prazo esgotado tem o motivo dele: a tela diz que o documento demorou, não que falhou.
    if (axios.isAxiosError(err) && err.code === 'ECONNABORTED') {
      throw new CorrecaoDoRascunhoError('pedido_tempo');
    }
    throw new CorrecaoDoRascunhoError(motivoDoErro(err));
  }
}

/** Os trechos do inventário pelo id, para a marca de edição de cada parte. */
export function trechosPorId(documento: DocumentoDaSaidaData): Map<string, TrechoNaTelaData> {
  return new Map(documento.trechos.map((t) => [t.id, t]));
}

/** As seções com título, para o índice da mesa. */
export function secoesDoIndice(documento: DocumentoDaSaidaData | null): SecaoDoDocumentoData[] {
  return (documento?.secoes ?? []).filter((s) => s.titulo !== null);
}

/**
 * E203.F5.T09: a âncora da seção do documento que imprime um dado da origem pedida (no bloco ou em
 * uma linha da tabela); `null` quando nenhuma seção o imprime.
 */
export function ancoraDaOrigem(
  documento: DocumentoDaSaidaData | null,
  quer: (origem: OrigemDoBlocoData) => boolean,
): string | null {
  for (const secao of documento?.secoes ?? []) {
    for (const bloco of secao.blocos) {
      const origens = [bloco.origem, ...(bloco.grade?.linhas ?? []).map((l) => l.origem)];
      if (origens.some((o) => o != null && quer(o))) return secao.ancora;
    }
  }
  return null;
}

/**
 * E205.F2.T02 (ADR-0147 §2): a seção do documento onde está o trecho (o bloco que o imprime ou, para
 * a nota escrita pelo perito, a seção da chave dele); `null` quando nenhuma o tem.
 */
export function secaoDoTrecho(
  documento: DocumentoDaSaidaData | null,
  trechoId: string,
): string | null {
  const secoes = documento?.secoes ?? [];
  const peloBloco = secoes.find((s) =>
    s.blocos.some((b) => b.partes.some((p) => p.trecho_id === trechoId)),
  );
  if (peloBloco !== undefined) return peloBloco.ancora;
  const chave = documento?.trechos.find((t) => t.id === trechoId)?.secao ?? null;
  if (chave === null) return null;
  return secoes.find((s) => s.chave === chave)?.ancora ?? null;
}
